import { useState, type FormEvent } from 'react';
import { Mail, Send, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useSubmitSupportRequest } from '@/api/extensions/useSupport';
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from '@/constants/support';

/**
 * In-app "contact support" form. Registered-users only — the backend pulls
 * the submitter's email from the session and sets it as the reply-to on the
 * message it sends to {@link SUPPORT_EMAIL}, so a support agent can just hit
 * reply. The client never sends an email address.
 *
 * Renders nothing for unauthenticated visitors (the Support page is already
 * behind a ProtectedRoute, but this keeps the component safe to drop in
 * elsewhere).
 */

const MIN_MESSAGE_LENGTH = 10;
const MAX_MESSAGE_LENGTH = 5000;

const CATEGORIES = [
  'General question',
  'Jackpots',
  'Rodeos',
  'Buddy groups',
  'Registration',
  'Payments',
  'Account & profile',
  'Bug report',
  'Other',
] as const;

function errorMessageFor(err: unknown): string {
  const e = err as { code?: string; message?: string } | undefined;
  switch (e?.code) {
    case 'NO_EMAIL_ON_FILE':
      return 'Your account has no email address on file. Add one to your profile before contacting support.';
    case 'MESSAGE_TOO_SHORT':
      return 'Please describe your issue in a little more detail.';
    case 'SEND_FAILED':
      return 'We couldn’t send your message right now. Please try again in a moment, or email us directly.';
    default:
      return e?.message || 'Something went wrong sending your message. Please try again.';
  }
}

export function SupportRequestForm() {
  const { isAuthenticated } = useAuth();
  const { showSuccess, showError } = useToast();
  const submit = useSubmitSupportRequest();

  const [category, setCategory] = useState<string>('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [touched, setTouched] = useState(false);

  if (!isAuthenticated) {
    return null;
  }

  const trimmed = message.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < MIN_MESSAGE_LENGTH;
  const canSubmit = trimmed.length >= MIN_MESSAGE_LENGTH && !submit.isPending;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    try {
      await submit.mutateAsync({
        category: category || undefined,
        message: trimmed,
      });
      setSubmitted(true);
      setMessage('');
      setCategory('');
      setTouched(false);
      showSuccess('Message sent', 'Our support team will get back to you by email.');
    } catch (err) {
      showError('Could not send message', errorMessageFor(err));
    }
  };

  if (submitted) {
    return (
      <div className="flex flex-col items-center gap-3 py-6 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/10">
          <CheckCircle2 className="h-6 w-6 text-green-600" />
        </div>
        <div>
          <p className="font-medium">Thanks — your message is on its way.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            We’ll reply to the email address on your account.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => setSubmitted(false)}>
          Send another message
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="support-category">Topic</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger id="support-category">
            <SelectValue placeholder="What's this about? (optional)" />
          </SelectTrigger>
          <SelectContent>
            {CATEGORIES.map((c) => (
              <SelectItem key={c} value={c}>
                {c}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label htmlFor="support-message">How can we help?</Label>
        <Textarea
          id="support-message"
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
          onBlur={() => setTouched(true)}
          placeholder="Describe your issue — include event names, dates, or anything else that helps us track it down."
          rows={6}
          aria-invalid={touched && tooShort}
        />
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span className={touched && tooShort ? 'text-destructive' : undefined}>
            {touched && tooShort
              ? `Please enter at least ${MIN_MESSAGE_LENGTH} characters.`
              : 'We’ll reply to the email on your account.'}
          </span>
          <span>
            {trimmed.length}/{MAX_MESSAGE_LENGTH}
          </span>
        </div>
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          Prefer email? Write to{' '}
          <a href={SUPPORT_MAILTO} className="underline hover:text-primary">
            {SUPPORT_EMAIL}
          </a>
          .
        </p>
        <Button type="submit" disabled={!canSubmit}>
          {submit.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending…
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              Send message
            </>
          )}
        </Button>
      </div>

      <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <Mail className="h-3 w-3" />
        Sent to the HighCall support team. Don’t include passwords or payment card numbers.
      </p>
    </form>
  );
}

export default SupportRequestForm;
